import axios from 'axios';

export const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '');

const API = axios.create({
  baseURL: API_BASE_URL,
});

// Add Authorization Token Interceptor
API.interceptors.request.use((req) => {
  const token = localStorage.getItem('token');
  if (token) {
    req.headers.Authorization = `Bearer ${token}`;
  }
  return req;
});
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest?._retry &&
      !originalRequest?.url?.includes('/users/login') &&
      !originalRequest?.url?.includes('/users/refresh-token')
    ) {
      const refreshToken = localStorage.getItem('refreshToken');

      if (refreshToken) {
        originalRequest._retry = true;

        try {
          const { data } = await API.post('/users/refresh-token', { refreshToken });
          localStorage.setItem('token', data.token);
          localStorage.setItem('refreshToken', data.refreshToken);
          localStorage.setItem('user', JSON.stringify(data));
          originalRequest.headers.Authorization = `Bearer ${data.token}`;
          return API(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem('refreshToken');
        }
      }
    }

    if (error.response && error.response.status === 401) {
        // Token expired or invalid, log out the user
        localStorage.removeItem('token'); // Remove token from localStorage
        localStorage.removeItem('refreshToken');

        // Trigger logout action in the component
        window.dispatchEvent(new Event('logout')); // Custom event to trigger logout
  
        window.location.href = '/login'; // Redirect to the login page
    }

    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);
// Auth-related APIs
export const loginUser = (data) => API.post('/users/login', data);
export const registerUser = (data) => API.post('/users/register', data);
export const changePassword = (data) => API.put('/users/change-password', data);
export const refreshAccessToken = (refreshToken) => API.post('/users/refresh-token', { refreshToken });

// User management APIs
export const fetchUsers = (page, limit) => 
  API.get('/users/getusers', { params: { page, limit } }); // Supports pagination

export const searchUsers = (search, page = 1, limit = 20) =>
  API.get('/users/search', { params: { search, page, limit } });

export const updateUser = (id, data) => API.put(`/users/users/${id}`, data); // Update user by ID
export const deleteUser = (id) => API.delete(`/users/users/${id}`); // Delete user by ID


// Push Notification-related APIs
export const enablePushNotifications = () => API.post('/notifications/enable');
export const disablePushNotifications = () => API.post('/notifications/disable');

export const rolesUser =() => API.get('/roles/');
export const departmentsUser = () => API.get('/departments/'); // New API for departments

export const createRole = (data) => API.post('/roles/', data);
export const updateRole = (id, data) => API.put(`/roles/${id}`, data);
export const deleteRole = (id) => API.delete(`/roles/${id}`);

export const createDepartment = (data) => API.post('/departments/', data);
export const updateDepartment = (id, data) => API.put(`/departments/${id}`, data);
export const deleteDepartment = (id) => API.delete(`/departments/${id}`);


// Incident-related APIs
export const getRoleBasedIncidents = (page = 1, limit = 10, status = 'open', extraParams = {}) =>
  API.get('/incidents/dashboard', { params: { page, limit, status, ...extraParams } });

export const createIncident = (data) => API.post('/incidents/create', data);

// export const updateIncident = (id, data) => API.put(`/incidents/update/${id}`, data);
export const updateIncident = (id, data) => {

  const formData = new FormData();

  formData.append('data', JSON.stringify(data));

  // USER COMMENT FILES
  if (data?.UserComment?.attachments) {
    data.UserComment.attachments.forEach(f => {
      if (f.fileObj) formData.append('files', f.fileObj);
    });
  }

  // HOD FILES
  if (data?.HODDetails?.attachments) {
    data.HODDetails.attachments.forEach(f => {
      if (f.fileObj) formData.append('files', f.fileObj);
    });
  }

  return API.put(`/incidents/update/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const getIncident = (id) => API.get(`/incidents/${id}`);
export const getIncidentStatusLog = (id) => API.get(`/incidents/${id}/status-log`);
export const getLatestIncidentRefNo = () => API.get('/incidents/getLatestRefno');

export const downloadIncidentExport = (id, type, params = {}) =>
  API.get(`/incidents/${id}/export/${type}`, {
    params,
    responseType: 'blob',
  });

export const getIncidentReports = (params = {}) =>
  API.get('/incidents/reports', { params });

export const downloadReportExcel = (params = {}) =>
  API.get('/incidents/reports/export/excel', {
    params,
    responseType: 'blob',
  });
